/**
 * Jargon-to-Human Translation Dictionary
 * 
 * Central configuration for converting technical terminology into
 * accessible labels with benefits and human-friendly metrics.
 * 
 * This enables the portal to show friendly labels by default while
 * keeping technical accuracy available via "Expert View" toggle.
 */

export interface JargonMapping {
  display: string;
  benefit?: string;
  summary?: string;
  human_metric?: string;
  analogy?: string;
  technical_detail?: string;
}

export interface MappingDictionary {
  [key: string]: JargonMapping;
}

export const mappings: MappingDictionary = {
  // ── Communication Protocols ──────────────────────────────────────
  ebus_opentherm: {
    display: "Smart Boiler Communication",
    benefit: "Enables 'cruise control' for your heating, saving up to 10% on fuel.",
    technical_detail: "load compensating thermostat",
  },
  
  load_compensation: {
    display: "Smart Temperature Matching",
    benefit: "Adjusts heat output based on outdoor temperature. Stops overshooting and reduces cycling.",
    technical_detail: "Weather compensation / Load compensation",
  },
  
  // ── Control Types ────────────────────────────────────────────────
  bimetallic_strip: {
    display: "Traditional Dial Control",
    benefit: "Replacing this old-style dial with a digital version stops 'temperature swings' and cuts bills by 15%.",
    technical_detail: "Bimetallic strip thermostat",
  },
  
  mechanical_thermostat: {
    display: "Basic Dial Thermostat",
    benefit: "Upgrading to smart controls provides better accuracy and remote access.",
    technical_detail: "Mechanical thermostat",
  },
  
  smart_load_comp: {
    display: "Smart Controls with Outdoor Sensing",
    benefit: "Modulates heat output to match real-time weather. Maximizes comfort and minimizes waste.",
    technical_detail: "Smart load-compensating thermostat",
  },
  
  // ── Hot Water Issues ─────────────────────────────────────────────
  dead_leg_8L: {
    display: "Pipework Heat Delay",
    summary: "8.0L water waste per draw",
    human_metric: "Approx. 10 wine bottles of water wasted every time you wait for the tap.",
    technical_detail: "8L dead leg volume",
  },
  
  combi_instant: {
    display: "Combi On-Demand Hot Water",
    benefit: "No cylinder standing losses, but combi taps still see lag and draw-off waste. Better suited to smaller households with low simultaneous demand.",
    technical_detail: "Combination boiler with on-demand DHW (tap lag + per-draw overhead)",
  },
  
  unvented_cylinder: {
    display: "High-Pressure Hot Water Tank",
    benefit: "Mains-pressure hot water with storage for multiple taps running simultaneously.",
    technical_detail: "Unvented hot water cylinder",
  },
  
  // ── Heat Loss & Insulation ───────────────────────────────────────
  heat_loss_140W: {
    display: "Building Retention Rating: Low",
    analogy: "Your home acts like a 'leaky bucket'—heat escapes quickly through the walls and roof.",
    technical_detail: "140W/m² heat loss coefficient",
  },
  
  poor_insulation: {
    display: "Thin Thermal Barrier",
    benefit: "Adding insulation is like putting on a winter coat—it keeps the warmth in and saves money.",
    technical_detail: "Poor thermal envelope / Low U-values",
  },
  
  epc_band_d: {
    display: "Average Energy Rating",
    benefit: "Your home is middle-of-the-road for energy efficiency. Improvements would reduce bills.",
    technical_detail: "EPC Band D",
  },
  
  // ── System Health ────────────────────────────────────────────────
  magnetite_sludge: {
    display: "Black Sludge Contamination",
    benefit: "Cleaning this out prevents blockages and extends equipment life by years.",
    analogy: "Like cholesterol in arteries—it restricts flow and damages components.",
    technical_detail: "Magnetite corrosion debris",
  },
  
  powerflush: {
    display: "Deep System Clean",
    benefit: "High-velocity flush removes accumulated debris. Restores flow and efficiency.",
    technical_detail: "Powerflush / System flush",
  },
  
  magnetic_filter: {
    display: "Debris Trap",
    benefit: "Catches metal particles before they damage the heat exchanger. Low maintenance.",
    technical_detail: "Magnetic system filter",
  },
  
  // ── Flow & Distribution ──────────────────────────────────────────
  laminar_flow: {
    display: "Smooth Water Flow",
    benefit: "Clean pipework with aligned flow paths. No turbulence means maximum heat transfer.",
    analogy: "Like a river flowing smoothly instead of crashing over rocks.",
    technical_detail: "Laminar flow regime",
  },
  
  microbore_8mm: {
    display: "Narrow Pipework",
    benefit: "Small pipes restrict flow. Upgrading to 22mm eliminates this bottleneck.",
    technical_detail: "8mm microbore distribution",
  },
  
  s_plan_zoning: {
    display: "Independent Heating Zones",
    benefit: "Heating and hot water run separately. No priority valve delays.",
    technical_detail: "S-Plan configuration",
  },
  
  // ── Efficiency Metrics ───────────────────────────────────────────
  sedbuk_band_a: {
    display: "Excellent Efficiency (90%+)",
    benefit: "Modern condensing boiler. Extracts maximum heat from fuel.",
    technical_detail: "SEDBUK Band A (≥90% efficiency)",
  },
  
  sedbuk_band_g: {
    display: "Poor Efficiency (<70%)",
    benefit: "Old boiler wastes significant fuel. Upgrading would cut bills by 30%+.",
    technical_detail: "SEDBUK Band G (<70% efficiency)",
  },
  
  cop_35: {
    display: "Heat Output to Input Ratio: 3.5×",
    benefit: "For every 1kW of electricity, you get 3.5kW of heat. Very efficient.",
    technical_detail: "COP 3.5 (Coefficient of Performance)",
  },
  
  // ── Heat Pumps ───────────────────────────────────────────────────
  ashp: {
    display: "Electric Heat Pump (Outdoor Unit)",
    benefit: "Extracts heat from outside air. Low carbon, but requires good insulation.",
    technical_detail: "Air Source Heat Pump (ASHP)",
  },
  
  low_temp_55c: {
    display: "Gentle Heat (55°C)",
    benefit: "Lower water temperature runs more efficiently. Needs larger radiators.",
    technical_detail: "55°C flow temperature",
  },
  
  bus_grant: {
    display: "£7,500 Government Grant",
    benefit: "Boiler Upgrade Scheme covers most of the heat pump cost difference.",
    technical_detail: "BUS Grant eligibility",
  },
  
  // ── System Types ─────────────────────────────────────────────────
  sealed_system: {
    display: "Pressurized System (No Header Tank)",
    benefit: "More compact, better flow rates, and easier to maintain.",
    technical_detail: "Sealed heating system",
  },
  
  open_vented: {
    display: "Gravity-Fed System (Header Tank)",
    benefit: "Simple and reliable, but limited flow and requires loft space.",
    technical_detail: "Open vented heating system",
  },
  
  // ── Emitters ─────────────────────────────────────────────────────
  oversized_radiators: {
    display: "Large Surface Area Radiators",
    benefit: "Big radiators can run cooler and still heat the room. Perfect for heat pumps.",
    technical_detail: "Oversized heat emitters",
  },
  
  underfloor_heating: {
    display: "Floor Heating",
    benefit: "Gentle radiant heat from below. Very efficient with heat pumps.",
    technical_detail: "Underfloor heating (UFH)",
  },
  
  // ── Water Quality ────────────────────────────────────────────────
  hard_water_scaling: {
    display: "Limescale Buildup",
    benefit: "Scale coats the heat exchanger like kettle fur. Reduces efficiency and shortens life.",
    technical_detail: "Hard water scaling / Calcium carbonate deposits",
  },
  
  scale_inhibitor: {
    display: "Chemical Limescale Protection",
    benefit: "Prevents scale formation in hard water areas. Extends boiler life.",
    technical_detail: "Scale inhibitor / Descaler dosing",
  },
};

/**
 * Normalize technical term to mapping key format.
 */
function normalizeKey(technicalTerm: string): string {
  return technicalTerm.toLowerCase().replace(/[\s-]/g, '_');
}

/**
 * Get human-friendly label for a technical term.
 * Returns undefined if no mapping exists.
 */
export function getDisplayLabel(technicalTerm: string): string | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key]?.display;
}

/**
 * Get benefit explanation for a technical term.
 */
export function getBenefit(technicalTerm: string): string | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key]?.benefit;
}

/**
 * Get human metric for a technical term.
 */
export function getHumanMetric(technicalTerm: string): string | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key]?.human_metric;
}

/**
 * Get analogy for a technical term.
 */
export function getAnalogy(technicalTerm: string): string | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key]?.analogy;
}

/**
 * Get technical detail for a term (used in expert view).
 */
export function getTechnicalDetail(technicalTerm: string): string | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key]?.technical_detail;
}

/**
 * Get complete mapping for a technical term.
 */
export function getMapping(technicalTerm: string): JargonMapping | undefined {
  const key = normalizeKey(technicalTerm);
  return mappings[key];
}
